import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Shirt, AlertTriangle, Swords, MessageSquareX, Volume2, Hammer,
    Clock, Clock3, DoorOpen, Coffee, ArrowLeft, ShieldAlert, Check,
    HeartPulse, Siren, Ambulance, Scissors, Droplet, Cigarette, School, Trash2
} from "lucide-react";

const ICON_MAP = {
    shirt: Shirt,
    "alert-triangle": AlertTriangle,
    swords: Swords,
    "message-square-x": MessageSquareX,
    "volume-2": Volume2,
    hammer: Hammer,
    clock: Clock,
    "clock-alert": Clock3,
    "door-open": DoorOpen,
    coffee: Coffee,
    "heart-pulse": HeartPulse,
    siren: Siren,
    ambulance: Ambulance,
    scissors: Scissors,
    droplet: Droplet,
    cigarette: Cigarette,
    school: School,
};

const today = () => new Date().toISOString().slice(0, 10);

export default function SigurimiIncident() {
    const { kind, id } = useParams();
    const nav = useNavigate();
    const [person, setPerson] = useState(null);
    const [types, setTypes] = useState([]);
    const [recent, setRecent] = useState([]);
    const [picked, setPicked] = useState(null);
    const [date, setDate] = useState(today());
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [t, ppl, inc] = await Promise.all([
                    api.get("/sigurimi/incident-types"),
                    api.get("/sigurimi/people", { params: { kind } }),
                    api.get("/sigurimi/incidents", { params: { subject_type: kind, subject_id: id } }),
                ]);
                setTypes(t.data[kind] || []);
                const p = (ppl.data || []).find((x) => x.id === id);
                setPerson(p || null);
                setRecent((inc.data || []).slice(0, 8));
            } catch (e) { setErr(e.response?.data?.detail || e.message); }
        })();
    }, [kind, id]);

    const submit = async () => {
        if (!picked) { setErr("Zgjidh llojin e incidentit"); return; }
        setBusy(true); setErr("");
        try {
            await api.post("/sigurimi/incident", {
                subject_type: kind,
                subject_id: id,
                incident_type: picked,
                date,
                note: note.trim() || null,
            });
            setSaved(true);
            setPicked(null); setNote(""); setDate(today());
            // refetch recent
            const inc = await api.get("/sigurimi/incidents", { params: { subject_type: kind, subject_id: id } });
            setRecent((inc.data || []).slice(0, 8));
            setTimeout(() => setSaved(false), 2200);
        } catch (e) { setErr(e.response?.data?.detail || e.message); }
        finally { setBusy(false); }
    };

    const delIncident = async (iid) => {
        if (!window.confirm("Fshi këtë incident?")) return;
        try {
            await api.delete(`/sigurimi/incident/${iid}`);
            setRecent((r) => r.filter((x) => x.id !== iid));
        } catch (e) { alert("Gabim: " + (e.response?.data?.detail || e.message)); }
    };

    if (!person) return <Layout><div className="text-slate-500">Duke ngarkuar…</div></Layout>;

    const initials = person.name.split(" ").map((n) => n[0]).slice(0, 2).join("");

    return (
        <Layout>
            <Link to="/sigurimi" className="inline-flex items-center gap-2 mb-4 px-4 py-2.5 rounded-xl bg-white border-2 border-slate-300 hover:border-oxford hover:bg-oxford hover:text-white text-slate-700 font-bold text-sm transition shadow-sm" data-testid="sigi-back">
                <ArrowLeft className="w-5 h-5" /> Mbrapa te Lista
            </Link>

            <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 mb-4 flex items-center gap-3" data-testid="sigi-person-card">
                <div className={`w-14 h-14 rounded-2xl ${kind === "student" ? "bg-oxford/10 text-oxford" : "bg-sage/15 text-sage"} flex items-center justify-center font-bold text-base shrink-0`}>{initials}</div>
                <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">{kind === "student" ? "Nxënës" : "Mësues"}</div>
                    <div className="font-heading text-xl font-bold text-slate-900 truncate">{person.name}</div>
                    <div className="text-xs text-slate-500 truncate">{kind === "student" ? (person.class_name || "Pa klasë") : (person.email || "—")}</div>
                </div>
            </div>

            {saved && (
                <div className="bg-sage/15 border border-sage/30 text-sage rounded-lg px-3 py-2 mb-3 text-sm font-bold inline-flex items-center gap-2" data-testid="sigi-saved-toast">
                    <Check className="w-4 h-4" /> Incidenti u regjistrua
                </div>
            )}

            <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 mb-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-3">Lloji i incidentit</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4" data-testid="sigi-types-grid">
                    {types.map((t) => {
                        const Icon = ICON_MAP[t.icon] || AlertTriangle;
                        const active = picked === t.code;
                        return (
                            <button key={t.code} onClick={() => setPicked(t.code)}
                                className={`p-3 rounded-xl border-2 text-left transition ${active ? "bg-terracotta text-white border-terracotta shadow-md" : "bg-white border-slate-200 hover:border-terracotta/50"}`}
                                data-testid={`sigi-type-${t.code}`}>
                                <Icon className={`w-5 h-5 mb-1.5 ${active ? "text-white" : "text-terracotta"}`} />
                                <div className="text-xs font-bold leading-tight">{t.label}</div>
                            </button>
                        );
                    })}
                </div>

                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Data</label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mb-3" data-testid="sigi-date" />
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Shënim (opsional)</label>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Detaje të mëtejshme…"
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="sigi-note" />

                {err && <div className="text-xs text-terracotta mt-2" data-testid="sigi-err">{err}</div>}

                <div className="mt-4 flex gap-2">
                    <Button onClick={() => nav("/sigurimi")} variant="outline" className="flex-1" data-testid="sigi-cancel">Anulo</Button>
                    <Button onClick={submit} disabled={busy || !picked} className="flex-1 bg-terracotta hover:bg-terracotta/90 text-white gap-1.5" data-testid="sigi-save">
                        <ShieldAlert className="w-4 h-4" />
                        {busy ? "Duke ruajtur…" : "Regjistro"}
                    </Button>
                </div>
            </div>

            {recent.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5" data-testid="sigi-history">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Historiku i fundit ({recent.length})</div>
                    <div className="divide-y divide-slate-200">
                        {recent.map((r) => {
                            const t = types.find((x) => x.code === r.incident_type);
                            const Icon = t ? (ICON_MAP[t.icon] || AlertTriangle) : AlertTriangle;
                            return (
                                <div key={r.id} className="py-2 flex items-start gap-2" data-testid={`sigi-history-${r.id}`}>
                                    <Icon className="w-4 h-4 text-terracotta mt-0.5 shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs font-bold text-slate-900">{t?.label || r.incident_type}</div>
                                        <div className="text-[11px] text-slate-500">{r.date}</div>
                                        {r.note && <div className="text-[11px] text-slate-700 italic mt-0.5">"{r.note}"</div>}
                                    </div>
                                    <button onClick={() => delIncident(r.id)} className="text-slate-400 hover:text-terracotta shrink-0 p-1" title="Fshi" data-testid={`sigi-del-${r.id}`}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </Layout>
    );
}
